import React, { useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  PeopleAlt as PeopleAltIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  LockOutlined as LockOutlinedIcon,
  MeetingRoom as MeetingRoomIcon,
  AttachMoney as AttachMoneyIcon,
  Campaign as CampaignIcon,
  BarChart as BarChartIcon,
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { blue } from "@mui/material/colors";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import useAdminContext from "../../store/contexts/AdminContext";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";

const drawerWidth = 260;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(2),
      marginLeft: 0,
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
      [theme.breakpoints.down("md")]: {
        marginLeft: 0,
      },
    }),
  })
);

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  backgroundColor: "#ffffff",
  color: "#1a1a1a",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const StyledNavLink = styled(NavLink)({
  textDecoration: "none",
  color: "inherit",
  display: "block",
  "&:hover": {
    textDecoration: "none",
  },
  "&.active .MuiListItem-root": {
    backgroundColor: blue[50],
    borderRight: `3px solid ${blue[500]}`,
    "& .MuiListItemText-primary": {
      color: blue[700],
      fontWeight: 600,
    },
    "& .MuiListItemIcon-root": {
      color: blue[500],
    },
  },
});

const ADMIN_ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
};

const AdminLayout = ({ children }) => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { admin, logout, notifications } = useAdminContext();
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleNotificationOpen = (event) =>
    setNotificationAnchor(event.currentTarget);
  const handleNotificationClose = () => setNotificationAnchor(null);

  const isSuperAdmin = () => admin?.role === ADMIN_ROLES.SUPER_ADMIN;

  const getRoleTitle = (role) => {
    switch (role) {
      case ADMIN_ROLES.SUPER_ADMIN:
        return "Super Administrator";
      case ADMIN_ROLES.ADMIN:
        return "Administrator";
      default:
        return "Admin";
    }
  };

  const handleLogout = () => {
    logout();
    history.push(ADMIN_ROUTES.login);
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    history.push(ADMIN_ROUTES.profile);
  };

  const handleChangePassword = () => {
    handleProfileMenuClose();
    history.push(ADMIN_ROUTES.changePassword);
  };

  const unreadNotifications = notifications?.filter((n) => !n.isRead)?.length || 0;

  // Menu items configuration (must be defined before drawerContent)
  const menuItems = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      path: ADMIN_ROUTES.dashboard,
      visible: true,
    },
    {
      text: "Advertisements",
      icon: <CampaignIcon />,
      path: ADMIN_ROUTES.ads,
      visible: true,
    },
    {
      text: "Analytics",
      icon: <BarChartIcon />,
      path: ADMIN_ROUTES.analytics,
      visible: true,
    },
    {
      text: "Admins",
      icon: <AdminPanelSettingsIcon />,
      path: ADMIN_ROUTES.admins,
      visible: isSuperAdmin(),
    },
    {
      text: "Platform Economy",
      icon: <AccountBalanceIcon />,
      path: ADMIN_ROUTES.economy,
      visible: isSuperAdmin(),
    },
    {
      text: "Users",
      icon: <PeopleAltIcon />,
      path: ADMIN_ROUTES.users,
      visible: true,
    },
    {
      text: "Rooms",
      icon: <MeetingRoomIcon />,
      path: ADMIN_ROUTES.rooms,
      visible: true,
    },
    {
      text: "Sessions",
      icon: <PersonIcon />,
      path: ADMIN_ROUTES.sessions,
      visible: true,
    },
    {
      text: "Payments",
      icon: <AttachMoneyIcon />,
      path: ADMIN_ROUTES.payments,
      visible: true,
    },
    {
      text: "Activity Logs",
      icon: <AssignmentIcon />,
      path: ADMIN_ROUTES.logs,
      visible: true,
    },
    {
      text: "Reports",
      icon: <DescriptionIcon />,
      path: ADMIN_ROUTES.reports,
      visible: true,
    },
    {
      text: "Notifications",
      icon: <NotificationsIcon />,
      path: ADMIN_ROUTES.notifications,
      visible: true,
    },
  ];

  // Drawer content (shared between mobile and desktop)
  const drawerContent = (
    <>
      <DrawerHeader>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            px: 2,
          }}
        >
          <Avatar sx={{ mr: 2, bgcolor: blue[700] }}>
            <LockOutlinedIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ color: "#1a1a1a", fontWeight: 700 }}>
              AdminPanel
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getRoleTitle(admin?.role)}
            </Typography>
          </Box>
          <IconButton onClick={handleDrawerClose} sx={{ display: { xs: "none", md: "block" } }}>
            {theme.direction === "ltr" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
          <IconButton onClick={handleDrawerToggle} sx={{ display: { xs: "block", md: "none" } }}>
            <ChevronLeftIcon />
          </IconButton>
        </Box>
      </DrawerHeader>
      <Divider />

      <List>
        {menuItems
          .filter((item) => item.visible)
          .map((item) => (
            <StyledNavLink
              key={item.path}
              to={item.path}
              exact
              activeClassName="active"
              onClick={() => {
                // Close mobile drawer when clicking link
                if (window.innerWidth < 960) {
                  setMobileOpen(false);
                }
              }}
            >
              <ListItem button>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            </StyledNavLink>
          ))}
      </List>

      <Divider />

      <List>
        <StyledNavLink
          to={ADMIN_ROUTES.settings}
          activeClassName="active"
          onClick={() => {
            if (window.innerWidth < 960) {
              setMobileOpen(false);
            }
          }}
        >
          <ListItem button>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItem>
        </StyledNavLink>

        <ListItem button onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex", backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <AppBarStyled position="fixed" open={open}>
        <Toolbar>
          {/* Desktop menu toggle */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              mr: 2,
              display: { xs: "none", md: "block" },
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          {/* Mobile menu toggle */}
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2, display: { xs: "block", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, color: "#1a1a1a", fontWeight: 600 }}
          >
            Admin Panel
          </Typography>

          {/* Notifications */}
          <IconButton
            color="inherit"
            onClick={handleNotificationOpen}
            sx={{ mr: 2 }}
          >
            <Badge badgeContent={unreadNotifications} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* Profile */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{
                mr: 2,
                color: "#555",
                display: { xs: "none", sm: "block" },
              }}
            >
              {admin?.username || "Admin"}
            </Typography>
            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
              <Avatar sx={{ bgcolor: blue[500] }}>
                {admin?.username?.charAt(0).toUpperCase() || "A"}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBarStyled>

      {/* Desktop Drawer */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
            borderRight: "1px solid #e0e0e0",
            boxShadow: "2px 0 12px rgba(0,0,0,0.08)",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        {drawerContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
          },
        }}
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleProfileClick}>
          <PersonIcon sx={{ mr: 1 }} /> Profile
        </MenuItem>
        <MenuItem onClick={handleChangePassword}>
          <LockOutlinedIcon sx={{ mr: 1 }} /> Change Password
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} /> Logout
        </MenuItem>
      </Menu>

      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ sx: { maxHeight: 400, width: 320 } }}
      >
        {notifications && notifications.length > 0 ? (
          notifications.slice(0, 5).map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={handleNotificationClose}
              sx={{ whiteSpace: "normal", py: 1.5 }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight="600">
                  {notification.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {notification.message}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2">No notifications</Typography>
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            handleNotificationClose();
            history.push(ADMIN_ROUTES.notifications);
          }}
        >
          <Typography variant="body2" color="primary" align="center" width="100%">
            View all notifications
          </Typography>
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Main open={open}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
};

export default AdminLayout;
